/**
 * События FTD-воронки сайта в collector-proxy — те же имена, step и атрибуты,
 * что уже шлёт рум (jackpoker-room), по контракту FTD_FUNNEL_EVENTS.md:
 *
 *  - site.registration_form.click.finished — клик по любой кнопке регистрации;
 *  - site.registration_form.load.finished / .cancelled — форма или виджет
 *    регистрации открылись (или игрок закрыл их раньше);
 *  - site.registration.submit.finished — регистрация (форма, Google, Telegram,
 *    виджеты value-chain и быстрой регистрации);
 *  - site.lootbox.* — лутбокс на /ftd (сейчас на сайте выключен; код оставлен,
 *    чтобы при включении события пошли без доработок);
 *  - атрибуты пути (ftd.journey_id, skin_name, client.site, auth.state) в LCP
 *    сайта — это этап «зашли на сайт».
 *
 * После регистрации игрок уходит в рум — путь продолжается там: ftd_jid
 * передаётся в ссылку на рум (сервер берёт его из cookie `ftd_jid`, см.
 * routes/api.get-room-url*.ts), рум подхватывает его из адресной строки.
 *
 * Правила модуля:
 *  - наблюдаемость не ломает страницу: любая ошибка SDK/хранилищ глушится;
 *  - события, отправленные до конфигурации клиента (клик до гидрации root),
 *    копятся в очереди и уходят после `configureFtdTelemetry`;
 *  - персональных данных нет: только идентификаторы (journey, EvenBet id).
 */
import { createTraceContext } from "@qws-org/collector-browser-sdk";

import {
  type CollectorProxyClient as Client,
  getSiteCollectorClient,
} from "~/shared/utils/collector-proxy/client";
import { uuidV4 } from "~/shared/utils/uuid";

type EventInput = Parameters<Client["sendAnalyticsEvent"]>[0]["event"];

export type FtdAttributeValue = string | number | boolean | null;
export type FtdAttributes = Record<string, FtdAttributeValue | undefined>;

type FtdStatus =
  | "ok"
  | "error"
  | "timeout"
  | "validation_error"
  | "rate_limit"
  | "unauthorized"
  | "upstream_error";

type FtdResult =
  | "success"
  | "validation_error"
  | "error"
  | "timeout"
  | "cancelled"
  | "redirected";

export type FtdRun = {
  scenarioId: string;
  scenarioRunId: string;
  traceId: string;
};

export type FtdOutcome =
  | {
      kind: "success";
      result?: "success" | "redirected";
      userId?: string;
      attributes?: FtdAttributes;
    }
  | {
      /** Отказ по данным игрока, капча, лимит — не технический сбой. */
      kind: "rejected";
      code: string;
      status?: "validation_error" | "rate_limit" | "unauthorized";
      attributes?: FtdAttributes;
    }
  | {
      kind: "error";
      code: string;
      status?: "error" | "upstream_error" | "timeout";
      attributes?: FtdAttributes;
    };

export type FtdStepHandle = {
  finish: (outcome: FtdOutcome) => void;
  /** Игрок ушёл до конца шага: `<шаг>.cancelled`, `error.code = user_left`. */
  cancel: () => void;
  /** Шаг оказался не тем (например, Google-вход вместо регистрации) — без события. */
  discard: () => void;
  isDone: () => boolean;
  run: FtdRun;
};

export type FtdTelemetryConfig = {
  collectorProxyUrl?: string;
  siteEnvironment: string;
  siteBrand: string;
  /** Боты, синтетические аудиты, локальный запуск — события не шлём. */
  skipReporting: boolean;
  isLoggedIn: boolean;
  playerId?: string;
};

/** Шаг не завершился за 20 с — `timeout` (FTD_FUNNEL_EVENTS.md §4). */
const STEP_TIMEOUT_MS = 20_000;
/** Клик и загрузка формы склеиваются, если форма начала открываться в течение 5 с. */
const CLICK_TO_FORM_MS = 5_000;
/** Повторный клик в течение 1 с — тот же клик. */
const DOUBLE_CLICK_MS = 1_000;
const MAX_QUEUE = 100;

/** Имя cookie и localStorage-ключа пути игрока — то же, что читает рум и сервер сайта. */
export const FTD_JOURNEY_KEY = "ftd_jid";
const JOURNEY_CREATED_KEY = "ftd_jid_created";
const JOURNEY_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const AFTER_REGISTRATION_KEY = "ftd_after_registration";
const PLAYER_KEY = "ftd_player";
const LOOTBOX_KEY = "ftd_lootbox_run";
const LOOTBOX_ID = "site-ftd";
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isBrowser = (): boolean =>
  typeof window !== "undefined" && typeof document !== "undefined";

const silently = <T>(task: () => T, fallback: T): T => {
  try {
    return task();
  } catch {
    return fallback;
  }
};

// ---------------------------------------------------------------------------
// Хранилища

const storageGet = (storage: "local" | "session", key: string): string | null =>
  silently(
    () =>
      (storage === "local"
        ? window.localStorage
        : window.sessionStorage
      ).getItem(key),
    null,
  );

const storageSet = (
  storage: "local" | "session",
  key: string,
  value: string | null,
): void => {
  silently(() => {
    const target =
      storage === "local" ? window.localStorage : window.sessionStorage;
    if (value === null) target.removeItem(key);
    else target.setItem(key, value);
  }, undefined);
};

const readCookie = (name: string): string | null =>
  silently(() => {
    const prefix = `${name}=`;
    const found = document.cookie
      .split("; ")
      .find((part) => part.startsWith(prefix));
    return found ? decodeURIComponent(found.slice(prefix.length)) : null;
  }, null);

const writeCookie = (name: string, value: string, maxAgeMs: number): void => {
  silently(() => {
    const maxAge = Math.max(0, Math.floor(maxAgeMs / 1000));
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
  }, undefined);
};

// ---------------------------------------------------------------------------
// Путь игрока: ftd.journey_id

let journeyId: string | null = null;

/**
 * `ftd.journey_id`: localStorage + cookie `ftd_jid`, 30 дней. Пришёл с
 * параметром `ftd_jid` (переход из рума/кассы) — сохраняем, если своего ещё
 * нет, и убираем параметр из адресной строки.
 */
export const getFtdJourneyId = (): string => {
  if (journeyId) return journeyId;
  if (!isBrowser()) return "";

  const url = silently(() => new URL(window.location.href), null);
  const fromUrl = url?.searchParams.get(FTD_JOURNEY_KEY) ?? null;
  const createdAt = Number(storageGet("local", JOURNEY_CREATED_KEY) ?? "0");
  const storedLocal = storageGet("local", FTD_JOURNEY_KEY);
  const stored =
    storedLocal && Date.now() - createdAt < JOURNEY_TTL_MS
      ? storedLocal
      : readCookie(FTD_JOURNEY_KEY);

  let id = stored && UUID_PATTERN.test(stored) ? stored : null;
  let created = id === storedLocal ? createdAt : Date.now();
  if (!id && fromUrl && UUID_PATTERN.test(fromUrl)) {
    id = fromUrl.toLowerCase();
    created = Date.now();
  }
  if (!id) {
    id = uuidV4();
    created = Date.now();
  }

  journeyId = id;
  storageSet("local", FTD_JOURNEY_KEY, id);
  storageSet("local", JOURNEY_CREATED_KEY, String(created));
  writeCookie(FTD_JOURNEY_KEY, id, created + JOURNEY_TTL_MS - Date.now());

  if (url && fromUrl !== null) {
    url.searchParams.delete(FTD_JOURNEY_KEY);
    silently(() => {
      window.history.replaceState(
        window.history.state,
        "",
        `${url.pathname}${url.search}${url.hash}`,
      );
    }, undefined);
  }

  return id;
};

// ---------------------------------------------------------------------------
// Общие атрибуты (FTD_FUNNEL_EVENTS.md §2)

const SKIN_BY_HOST: ReadonlyArray<readonly [RegExp, string]> = [
  [/(^|\.)jack-?poker\d*\./, "jackpoker"],
  [/(^|\.)jack-(playnow|playcard|set)\./, "jackpoker"],
  [/(^|\.)croreplay\./, "croreplay"],
  [/(^|\.)(fullhousecrypto|wpa2bbek)\./, "fullhousecrypto"],
];

const getSite = (): string =>
  isBrowser() ? window.location.hostname.replace(/^www\./, "") : "";

export const getFtdSkinName = (site = getSite()): string => {
  const found = SKIN_BY_HOST.find(([pattern]) => pattern.test(site));
  return found ? found[1] : (site.split(".")[0] ?? "");
};

/**
 * Добавляет `ftd_jid` к ссылке на рум/кассу, чтобы путь игрока не рвался при
 * переходе. Только поддомены своего сайта (room.jack-poker.com,
 * lobby.croreplay.com) и домены того же скина; сторонним сайтам (соцсети,
 * сторы, партнёры) идентификатор не передаём. Ссылки на свой origin не меняем:
 * серверные роуты /api/get-room-url* добавляют параметр сами из cookie.
 */
export const withFtdJourneyParam = (href: string): string => {
  if (!isBrowser() || !href) return href;

  return silently(() => {
    const target = new URL(href, window.location.href);
    if (!/^https?:$/.test(target.protocol)) return href;
    if (target.origin === window.location.origin) return href;
    const site = getSite();
    const host = target.hostname.replace(/^www\./, "");
    const sameSkin =
      host === site ||
      host.endsWith(`.${site}`) ||
      getFtdSkinName(host) === getFtdSkinName(site);
    if (!site || !sameSkin) return href;
    if (target.searchParams.has(FTD_JOURNEY_KEY)) return href;

    const id = getFtdJourneyId();
    if (!id) return href;
    target.searchParams.set(FTD_JOURNEY_KEY, id);
    return target.toString();
  }, href);
};

const getDeviceType = (): "mobile" | "tablet" | "desktop" => {
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width <= 1024) return "tablet";
  return "desktop";
};

let isLoggedIn = false;
let playerId: string | null = null;

const getPlayerId = (): string | null =>
  playerId ?? storageGet("session", PLAYER_KEY);

export const setFtdPlayer = (id: string | null | undefined): void => {
  if (!id || id === "-1") return;
  playerId = id;
  isLoggedIn = true;
  storageSet("session", PLAYER_KEY, id);
};

/** Атрибуты пути для чужих событий (LCP из web-vitals). */
export const getFtdCommonAttributes = (): Record<string, FtdAttributeValue> => {
  if (!isBrowser()) return {};
  const attributes: Record<string, FtdAttributeValue> = {
    skin_name: getFtdSkinName(),
    "client.site": getSite(),
    "ftd.journey_id": getFtdJourneyId(),
    "app.host": "site",
    page_path: window.location.pathname,
    "device.type": getDeviceType(),
    "auth.state": isLoggedIn ? "logged_in" : "guest",
  };
  const player = getPlayerId();
  if (player) attributes["player.ev_id"] = player;
  return attributes;
};

// ---------------------------------------------------------------------------
// Клиент и отправка

let client: Client | null = null;
let disabled = false;
const queue: Array<EventInput> = [];

const deliver = (event: EventInput): void => {
  if (disabled) return;
  if (!client) {
    if (queue.length < MAX_QUEUE) queue.push(event);
    return;
  }
  silently(() => client?.sendAnalyticsEvent({ event }), undefined);
};

export const startFtdRun = (scenarioId: string): FtdRun => ({
  scenarioId,
  scenarioRunId: uuidV4(),
  traceId: silently(() => createTraceContext().traceId, ""),
});

const compact = (
  attributes: FtdAttributes,
): Record<string, FtdAttributeValue> => {
  const result: Record<string, FtdAttributeValue> = {};
  Object.entries(attributes).forEach(([key, value]) => {
    if (value !== undefined) result[key] = value;
  });
  return result;
};

type EmitParams = {
  run: FtdRun;
  stepId: string;
  name: string;
  status?: FtdStatus;
  result?: FtdResult;
  durationMs?: number;
  errorCode?: string;
  userId?: string;
  attributes?: FtdAttributes;
};

const emit = ({
  run,
  stepId,
  name,
  status = "ok",
  result,
  durationMs,
  errorCode,
  userId,
  attributes = {},
}: EmitParams): void => {
  if (!isBrowser()) return;
  silently(() => {
    const code = status === "ok" ? undefined : (errorCode ?? "UNKNOWN");
    const span = run.traceId
      ? silently(() => createTraceContext({ traceId: run.traceId }), null)
      : null;
    const player = userId ?? getPlayerId() ?? undefined;

    const event: EventInput = {
      eventId: uuidV4(),
      timestamp: new Date().toISOString(),
      scenario: {
        scenarioId: run.scenarioId,
        scenarioRunId: run.scenarioRunId,
        stepId,
      },
      name,
      status,
      attributes: compact({
        ...getFtdCommonAttributes(),
        "player.ev_id": player,
        result: result ?? (status === "ok" ? "success" : "error"),
        errorCode: code,
        ...attributes,
      }),
    };
    if (span) event.trace = { traceId: span.traceId, spanId: span.spanId };
    if (durationMs !== undefined) {
      event.durationMs = Math.max(0, Math.round(durationMs));
    }
    if (code) event.error = { code };
    if (player) event.subject = { userId: player };

    deliver(event);
  }, undefined);
};

// ---------------------------------------------------------------------------
// Замер времени: performance.now() + была ли вкладка скрыта

let lastHiddenAt = -1;

const watchVisibility = (): void => {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") lastHiddenAt = performance.now();
  });
};

export type FtdTimer = {
  elapsedMs: () => number;
  wasHidden: () => boolean;
};

/** `startedAt` — значение performance.now() начала шага (по умолчанию сейчас). */
export const startFtdTimer = (startedAt?: number): FtdTimer => {
  const start = startedAt ?? silently(() => performance.now(), 0);
  const hiddenAtStart = silently(
    () => document.visibilityState === "hidden",
    false,
  );
  return {
    elapsedMs: () => silently(() => performance.now() - start, 0),
    wasHidden: () => hiddenAtStart || lastHiddenAt >= start,
  };
};

// ---------------------------------------------------------------------------
// Незавершённые шаги: при уходе со страницы — `<шаг>.cancelled`

const pendingOnLeave = new Map<string, () => void>();

const onPageHide = (): void => {
  const tasks = Array.from(pendingOnLeave.values());
  pendingOnLeave.clear();
  tasks.forEach((task) => silently(task, undefined));
};

/**
 * Страницу вернули из bfcache (назад/вперёд без перезагрузки): игра на /ftd
 * продолжается — после записанного ухода начинаем новый прогон лутбокса.
 */
const onPageShow = (event: PageTransitionEvent): void => {
  if (!event.persisted) return;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  resumeAbandonedLootbox();
};

let listenersInstalled = false;

const installListeners = (): void => {
  if (listenersInstalled || !isBrowser()) return;
  listenersInstalled = true;
  silently(() => {
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    watchVisibility();
  }, undefined);
};

type StartStepParams = {
  run: FtdRun;
  stepId: string;
  /** Имя без суффикса: `site.registration_form.load`. */
  name: string;
  timer?: FtdTimer;
  attributes?: FtdAttributes;
  timeoutCode?: string;
  timeoutMs?: number;
};

export const startFtdStep = ({
  run,
  stepId,
  name,
  timer = startFtdTimer(),
  attributes = {},
  timeoutCode = "load_timeout",
  timeoutMs = STEP_TIMEOUT_MS,
}: StartStepParams): FtdStepHandle => {
  installListeners();
  const key = `${name}:${run.scenarioRunId}:${stepId}`;
  let done = false;
  let timeoutId: number | undefined;

  const complete = (): boolean => {
    if (done) return false;
    done = true;
    pendingOnLeave.delete(key);
    window.clearTimeout(timeoutId);
    return true;
  };

  const cancel = (): void => {
    if (!complete()) return;
    emit({
      run,
      stepId,
      name: `${name}.cancelled`,
      status: "error",
      result: "cancelled",
      errorCode: "user_left",
      attributes,
    });
  };

  const finish = (outcome: FtdOutcome): void => {
    if (!complete()) return;
    const timing = {
      "page.was_hidden": timer.wasHidden(),
    };
    if (outcome.kind === "success") {
      emit({
        run,
        stepId,
        name: `${name}.finished`,
        result: outcome.result ?? "success",
        durationMs: timer.elapsedMs(),
        userId: outcome.userId,
        attributes: { ...attributes, ...timing, ...outcome.attributes },
      });
      return;
    }
    emit({
      run,
      stepId,
      name: `${name}.finished`,
      status:
        outcome.status ??
        (outcome.kind === "rejected" ? "validation_error" : "error"),
      result:
        outcome.kind === "rejected"
          ? "validation_error"
          : outcome.status === "timeout"
            ? "timeout"
            : "error",
      errorCode: outcome.code,
      durationMs: timer.elapsedMs(),
      attributes: { ...attributes, ...timing, ...outcome.attributes },
    });
  };

  const discard = (): void => {
    complete();
  };

  // Во вкладке в фоне браузер не рисует и не даёт rAF — ждём, пока она
  // вернётся на экран, иначе получим ложный таймаут.
  const onTimeout = (): void => {
    if (silently(() => document.visibilityState === "hidden", false)) {
      timeoutId = window.setTimeout(onTimeout, timeoutMs);
      return;
    }
    finish({ kind: "error", code: timeoutCode, status: "timeout" });
  };
  timeoutId = window.setTimeout(onTimeout, timeoutMs);
  pendingOnLeave.set(key, cancel);

  return { finish, cancel, discard, isDone: () => done, run };
};

// ---------------------------------------------------------------------------
// Конфигурация

/**
 * Вызывается один раз из root (FtdTelemetryInit). До вызова события копятся
 * в очереди; если отчётность выключена (бот, аудит, localhost) — очередь
 * сбрасывается и дальше ничего не шлётся.
 */
export const configureFtdTelemetry = ({
  collectorProxyUrl,
  siteEnvironment,
  siteBrand,
  skipReporting,
  isLoggedIn: loggedIn,
  playerId: player,
}: FtdTelemetryConfig): void => {
  if (!isBrowser()) return;

  // состояние входа обновляется при каждом вызове (root перечитывает данные
  // после логина/логаута), клиент создаётся один раз
  isLoggedIn = loggedIn;
  if (loggedIn) {
    setFtdPlayer(player);
  } else {
    // вышел из аккаунта в этой вкладке — старый EvenBet id не тянем
    playerId = null;
    storageSet("session", PLAYER_KEY, null);
  }
  if (client ?? disabled) return;
  getFtdJourneyId();
  installListeners();

  if (skipReporting || !collectorProxyUrl) {
    disabled = true;
    queue.length = 0;
    return;
  }

  // тот же клиент и те же параметры, что у Web Vitals (WebVitalsReporter)
  client = silently(
    () =>
      getSiteCollectorClient({ collectorProxyUrl, siteEnvironment, siteBrand }),
    null,
  );
  if (!client) {
    disabled = true;
    queue.length = 0;
    return;
  }

  queue.splice(0).forEach(deliver);
};

// ---------------------------------------------------------------------------
// Шаг 2а. Клик по любой кнопке регистрации

export type RegistrationClickTarget = "modal" | "room";

type ClickState = {
  run: FtdRun;
  at: number;
  location: string;
  target: RegistrationClickTarget;
};

let lastClick: ClickState | null = null;

/**
 * Любая кнопка регистрации — в хедере, баннере, футере, модалке входа.
 * `location` — где кнопка (header, footer, mainBannerV3, login_modal, …),
 * `target` — что откроется: модалка на сайте или переход в рум (фича-флаг
 * `registration-feature` выключен — регистрация пройдёт уже в руме).
 */
export const trackFtdRegistrationClick = ({
  buttonId,
  location,
  registrationType,
  target,
}: {
  buttonId: string;
  location: string;
  registrationType: string;
  target: RegistrationClickTarget;
}): void => {
  if (!isBrowser()) return;
  const now = silently(() => performance.now(), 0);
  if (lastClick && now - lastClick.at < DOUBLE_CLICK_MS) return;

  const run = startFtdRun("site.registration_form");
  lastClick = { run, at: now, location, target };
  emit({
    run,
    stepId: "reg_button_clicked",
    name: "site.registration_form.click.finished",
    durationMs: 0,
    attributes: {
      entry_point: "direct",
      "button.id": buttonId,
      "button.location": location,
      "registration.type": registrationType,
      "click.target": target,
    },
  });
};

// ---------------------------------------------------------------------------
// Шаг 2б. Загрузка формы регистрации

type FormLoadState = {
  handle: FtdStepHandle;
  entryPoint: string;
};

let formLoad: FormLoadState | null = null;
let formEntryPoint = "direct";

/**
 * Модалка регистрации начала открываться. Если перед этим был клик по кнопке
 * регистрации (до 5 с) — тот же scenarioRunId и замер от клика; иначе форма
 * открыта ссылкой (`openModal=register`) или из другого потока.
 */
export const startFtdRegistrationFormLoad = ({
  registrationType,
  loadMethod,
  openedBy,
}: {
  registrationType: string;
  loadMethod: "modal" | "widget" | "page";
  openedBy?: string;
}): void => {
  if (!isBrowser()) return;
  if (formLoad && !formLoad.handle.isDone()) return;

  const now = silently(() => performance.now(), 0);
  const click =
    lastClick &&
    lastClick.target === "modal" &&
    now - lastClick.at < CLICK_TO_FORM_MS
      ? lastClick
      : null;
  lastClick = null;

  const entryPoint = click?.location ?? openedBy ?? "direct";
  formEntryPoint = entryPoint;
  formLoad = {
    entryPoint,
    handle: startFtdStep({
      run: click?.run ?? startFtdRun("site.registration_form"),
      stepId: "scenario",
      name: "site.registration_form.load",
      timer: startFtdTimer(click?.at),
      attributes: {
        entry_point: entryPoint,
        load_method: loadMethod,
        "registration.type": registrationType,
      },
    }),
  };
};

/** Поля формы доступны для ввода (форма/виджет смонтированы). */
export const finishFtdRegistrationFormLoad = (): void => {
  formLoad?.handle.finish({ kind: "success" });
};

/** Виджет регистрации не загрузился: `widget_load_failed` и т.п. */
export const failFtdRegistrationFormLoad = (code: string): void => {
  formLoad?.handle.finish({ kind: "error", code });
};

/** Модалку закрыли до того, как форма загрузилась. */
export const cancelFtdRegistrationFormLoad = (): void => {
  if (formLoad && !formLoad.handle.isDone()) formLoad.handle.cancel();
};

// ---------------------------------------------------------------------------
// Шаг 3А. Регистрация

let validationAttempts = 0;

/** Клиентская валидация не пустила отправку формы (отдельным событием не шлём). */
export const noteFtdRegistrationValidationFailed = (): void => {
  validationAttempts += 1;
};

const markRegistered = (player: string | undefined): void => {
  setFtdPlayer(player);
  storageSet("session", AFTER_REGISTRATION_KEY, "1");
};

type HttpOutcome = { httpStatus?: number; serverMs?: number };

export type FtdRegistrationSubmit = {
  success: (params: { playerId?: string } & HttpOutcome) => void;
  rejected: (
    params: {
      code: string;
      status?: "validation_error" | "rate_limit" | "unauthorized";
    } & HttpOutcome,
  ) => void;
  failed: (
    params: { code: string; status?: "error" | "upstream_error" } & HttpOutcome,
  ) => void;
  /** Это оказался вход существующего игрока, а не регистрация. */
  discard: () => void;
};

/**
 * Отправка регистрации, прошедшая клиентскую валидацию: форма, Google,
 * Telegram. Замер — до показа результата (следующий экран или ошибка).
 */
export const startFtdRegistrationSubmit = ({
  method,
  promoPresent,
}: {
  method: "form" | "google" | "telegram";
  promoPresent?: boolean;
}): FtdRegistrationSubmit => {
  const attempts = validationAttempts;
  validationAttempts = 0;
  const handle = startFtdStep({
    run: startFtdRun("site.registration"),
    stepId: "scenario",
    name: "site.registration.submit",
    timeoutCode: "timeout",
    attributes: {
      entry_point: formEntryPoint,
      "registration.method": method,
      validation_attempts: attempts,
      "promo.present": promoPresent,
    },
  });

  const http = ({ httpStatus, serverMs }: HttpOutcome): FtdAttributes => ({
    "http.status_code": httpStatus,
    server_ms: serverMs === undefined ? undefined : Math.round(serverMs),
  });

  return {
    success: ({ playerId: player, ...rest }) => {
      markRegistered(player);
      handle.finish({
        kind: "success",
        userId: player,
        attributes: { ...http(rest), "player.ev_id": player },
      });
    },
    rejected: ({ code, status, ...rest }) => {
      handle.finish({ kind: "rejected", code, status, attributes: http(rest) });
    },
    failed: ({ code, status, ...rest }) => {
      handle.finish({ kind: "error", code, status, attributes: http(rest) });
    },
    discard: () => handle.discard(),
  };
};

/**
 * Шаг 3А для регистрации внутри виджета на сайте (value-chain, быстрая
 * регистрация): виджет — внешний скрипт, сайт видит только колбэк «готово»,
 * поэтому без замера: `durationMs = 0`, `duration.measured = false` (отчёт
 * не берёт такие события в скорость). OTP-виджет события не шлёт: шаг 3Б
 * отправляет otp-registration.
 */
const completedRegistrations = new Set<string>();

export const trackFtdWidgetRegistration = ({
  method,
  playerId: player,
}: {
  method: "value_chain" | "fast_registration";
  playerId?: string;
}): void => {
  if (!isBrowser()) return;
  const key = `${method}:${player ?? ""}`;
  if (completedRegistrations.has(key)) return;
  completedRegistrations.add(key);
  markRegistered(player);
  emit({
    run: startFtdRun("site.registration"),
    stepId: "scenario",
    name: "site.registration.submit.finished",
    durationMs: 0,
    userId: player,
    attributes: {
      entry_point: formEntryPoint,
      "registration.method": method,
      "duration.measured": false,
      "player.ev_id": player,
    },
  });
};

/**
 * Код HTTP-ответа регистрации → исход шага по §4 контракта:
 * 429 — лимит, 401/403 — нет доступа, 400/409/422 — отказ по данным игрока,
 * остальное — сбой.
 */
export const reportFtdRegistrationHttp = (
  submit: FtdRegistrationSubmit,
  { httpStatus, serverMs, code }: HttpOutcome & { code?: string },
): void => {
  if (httpStatus === 429) {
    submit.rejected({
      code: "rate_limited",
      status: "rate_limit",
      httpStatus,
      serverMs,
    });
  } else if (httpStatus === 401 || httpStatus === 403) {
    submit.rejected({
      code: code ?? `http_${httpStatus}`,
      status: "unauthorized",
      httpStatus,
      serverMs,
    });
  } else if (httpStatus === 400 || httpStatus === 409 || httpStatus === 422) {
    submit.rejected({
      code: code ?? `http_${httpStatus}`,
      httpStatus,
      serverMs,
    });
  } else {
    submit.failed({
      code:
        code ??
        (httpStatus === undefined
          ? "network_error"
          : httpStatus >= 500
            ? "http_5xx"
            : `http_${httpStatus}`),
      httpStatus,
      serverMs,
    });
  }
};

// ---------------------------------------------------------------------------
// Шаги 4–11. Лутбокс на сайте (/ftd). Сейчас на сайте выключен (после
// регистрации — сразу рум); код оставлен, чтобы включение не требовало доработок.

type LootboxState = {
  run: FtdRun;
  entryPoint: string;
  lastStep: string;
  finished: boolean;
  /** Прогон закрыт уходом со страницы (site.lootbox.cancelled). */
  abandoned?: boolean;
};

let lootbox: LootboxState | null = null;

const saveLootbox = (): void => {
  storageSet("session", LOOTBOX_KEY, lootbox ? JSON.stringify(lootbox) : null);
};

const loadLootbox = (): LootboxState | null => {
  if (lootbox) return lootbox;
  const raw = storageGet("session", LOOTBOX_KEY);
  if (!raw) return null;
  const parsed = silently(() => JSON.parse(raw) as LootboxState, null);
  if (!parsed?.run?.scenarioRunId) return null;
  lootbox = parsed;
  return lootbox;
};

const lootboxAbandonKey = "site.lootbox:abandon";

const armLootboxAbandon = (): void => {
  installListeners();
  pendingOnLeave.set(lootboxAbandonKey, () => {
    const state = loadLootbox();
    if (!state || state.finished) return;
    // Прогон закончен уходом; вернётся (в т.ч. перезагрузка) — новая игра.
    state.finished = true;
    state.abandoned = true;
    saveLootbox();
    emit({
      run: state.run,
      stepId: "abandoned",
      name: "site.lootbox.cancelled",
      status: "error",
      result: "cancelled",
      errorCode: "user_left",
      attributes: {
        entry_point: state.entryPoint,
        "lootbox.id": LOOTBOX_ID,
        "abandon.at_step": state.lastStep,
        "abandon.reason": "page_hide",
      },
    });
  });
};

function resumeAbandonedLootbox(): void {
  const state = loadLootbox();
  if (!state?.abandoned) return;
  if (!/\/ftd(\/|$)/.test(window.location.pathname)) return;
  lootbox = {
    ...state,
    run: startFtdRun("site.lootbox"),
    finished: false,
    abandoned: false,
  };
  saveLootbox();
  armLootboxAbandon();
}

const lootboxAttributes = (state: LootboxState): FtdAttributes => ({
  entry_point: state.entryPoint,
  "lootbox.id": LOOTBOX_ID,
});

const lootboxStepDone = (stepId: string): void => {
  const state = loadLootbox();
  if (!state) return;
  state.lastStep = stepId;
  if (stepId === "scenario") {
    state.finished = true;
    pendingOnLeave.delete(lootboxAbandonKey);
  }
  saveLootbox();
};

/**
 * Шаг 4: открылась страница /ftd (первый экран лутбокса). Новая игра — новый
 * scenarioRunId; перезагрузка страницы посреди игры — тот же.
 */
export const trackFtdLootboxIntroOpened = ({
  screen,
}: {
  screen: "country" | "game" | "cashier";
}): void => {
  if (!isBrowser()) return;
  const existing = loadLootbox();
  // Возврат на /ftd сразу в кассу (игра уже пройдена) — не новая игра.
  if (screen === "cashier" && (!existing || existing.finished)) return;
  if (!existing || existing.finished) {
    const afterRegistration =
      storageGet("session", AFTER_REGISTRATION_KEY) === "1";
    storageSet("session", AFTER_REGISTRATION_KEY, null);
    lootbox = {
      run: startFtdRun("site.lootbox"),
      entryPoint: afterRegistration ? "after_registration" : "direct",
      lastStep: "",
      finished: false,
    };
    saveLootbox();
  }
  const state = loadLootbox();
  if (!state) return;
  armLootboxAbandon();

  // От навигации (timeOrigin) до первого экрана после гидрации — только если
  // /ftd открыт загрузкой документа; при переходе внутри SPA замера нет.
  const documentPath = silently(() => {
    const [navigation] = performance.getEntriesByType("navigation");
    return navigation ? new URL(navigation.name).pathname : null;
  }, null);
  const measured = documentPath === window.location.pathname;
  const timer = startFtdTimer(0);
  emit({
    run: state.run,
    stepId: "intro_opened",
    name: "site.lootbox.intro.open.finished",
    durationMs: measured ? timer.elapsedMs() : 0,
    attributes: {
      ...lootboxAttributes(state),
      "lootbox.screen": screen,
      "page.was_hidden": timer.wasHidden(),
      "duration.measured": measured ? undefined : false,
    },
  });
  lootboxStepDone("intro_opened");
};

/** Шаг 5: игрок выбрал страну (время игрока — в скорость не идёт). */
export const trackFtdLootboxCountrySelected = ({
  countryCode,
  source,
  changed,
  durationMs,
}: {
  countryCode: string;
  source: "geo_prefill" | "saved" | "manual";
  changed: boolean;
  durationMs: number;
}): void => {
  const state = loadLootbox();
  if (!state) return;
  emit({
    run: state.run,
    stepId: "country_selected",
    name: "site.lootbox.country.select.finished",
    durationMs,
    attributes: {
      ...lootboxAttributes(state),
      "country.code": countryCode,
      "country.source": source,
      "country.changed": changed,
    },
  });
  lootboxStepDone("country_selected");
};

const startLootboxStep = (
  stepId: string,
  name: string,
  attributes: FtdAttributes = {},
): FtdStepHandle | null => {
  const state = loadLootbox();
  if (!state || state.finished) return null;
  const handle = startFtdStep({
    run: state.run,
    stepId,
    name,
    timeoutCode: "timeout",
    attributes: { ...lootboxAttributes(state), ...attributes },
  });
  return {
    ...handle,
    finish: (outcome) => {
      if (handle.isDone()) return;
      handle.finish(outcome);
      if (outcome.kind === "success") lootboxStepDone(stepId);
    },
  };
};

/** Шаг 6: «Выбрать страну» → ответ сервера и экран игры. */
export const startFtdLootboxCountryConfirm = (
  countryCode: string,
): FtdStepHandle | null =>
  startLootboxStep("country_confirmed", "site.lootbox.country.confirm", {
    "country.code": countryCode,
  });

/** Шаг 7: кнопка «Play» на стартовом экране игры → игра началась. */
export const startFtdLootboxOpen = (): FtdStepHandle | null =>
  startLootboxStep("lootbox_opened", "site.lootbox.open", {
    "button.id": "lootbox_play",
  });

/** Шаг 8: все карты открыты → награда на экране. */
export const startFtdLootboxRewardShown = (): FtdStepHandle | null =>
  startLootboxStep("reward_shown", "site.lootbox.reward.shown", {
    "reward.type": "bonus",
  });

/** Шаг 9: «Получить бонус» → бонус применён, экран предложения депозита. */
export const startFtdLootboxClaim = (rewardId: string): FtdStepHandle | null =>
  startLootboxStep("bonus_claimed", "site.lootbox.claim", {
    "reward.type": "bonus",
    "reward.id": rewardId,
    "claim.next": "deposit_offer",
  });

/** Шаг 10: предложение депозита отрисовано, кнопка «Депозит» активна. */
export const startFtdLootboxDepositOffer = (): FtdStepHandle | null =>
  startLootboxStep("deposit_offer_loaded", "site.lootbox.deposit_offer.load", {
    "offer.id": LOOTBOX_ID,
  });

let offerShownAt: number | null = null;

export const noteFtdLootboxOfferShown = (): void => {
  offerShownAt = silently(() => performance.now(), null);
};

/**
 * Шаг 11: клик «Депозит» — финал лутбокса (step `scenario`). Время игрока
 * (от показа оффера до клика), в скорость не идёт.
 */
export const trackFtdLootboxDepositClick = ({
  openMethod,
}: {
  openMethod: "widget" | "room_redirect";
}): void => {
  const state = loadLootbox();
  if (!state || state.finished) return;
  const now = silently(() => performance.now(), 0);
  emit({
    run: state.run,
    stepId: "scenario",
    name: "site.lootbox.deposit.click.finished",
    durationMs: offerShownAt === null ? 0 : now - offerShownAt,
    attributes: {
      ...lootboxAttributes(state),
      "offer.id": LOOTBOX_ID,
      "button.id": "lootbox_deposit",
      "cashier.open_method": openMethod,
    },
  });
  lootboxStepDone("scenario");
};
